import User from 'records/User';
import React, {useEffect, useState} from 'react';
import AuthStore from 'stores/AuthStore';
import UserSource from 'sources/UserSource';
import ToastActions from '@store/toast/ToastActions';
import {AvatarSource} from 'sources/AvatarSource';
import UserProfile from 'components/pure/admin/profile/UserProfile';
import {PasswordSource} from '@store/password/PasswordSource';
import Button from '@material-ui/core/Button';
import {UserProfileContextProvider, useUserProfileContext} from './ProfileContext';

interface EditingUser {
  user: User;
  version: number;
}

const UserProfileContainer: React.FC = () => {
  const [editingUser, setEditingUser] = useState<EditingUser>();
  const [pristineUser, setPristineUser] = useState<User>();
  const [isPristine, setIsPristine] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const {errors} = useUserProfileContext();
  const formIsValid = !Object.values(errors).some(isError => isError);

  const authUserId = AuthStore.getState().user.id;

  useEffect(() => {
    UserSource.fetchSimple(authUserId).then(u => {
      setEditingUser({
        user: u,
        version: editingUser?.version ?? 0,
      });
      setPristineUser(u);
    });
  }, [authUserId, editingUser?.version]);

  if (!editingUser?.user) return null;

  const onEditUser = (u: User) => {
    setEditingUser({
      user: u,
      version: editingUser?.version,
    });
    setIsPristine(false);
  };

  const resetUser = () => {
    pristineUser &&
      setEditingUser({
        user: pristineUser,
        version: editingUser?.version,
      });
    setIsPristine(true);
  };

  const onSaveUser = () => {
    setIsSaving(true);
    return UserSource.update(editingUser.user).then(user => {
      setIsSaving(false);
      const newEditingUser = {
        user: user,
        version: editingUser!.version! + 1,
      };
      setEditingUser(newEditingUser);
      ToastActions.addToast({
        message: 'User Info Saved',
        time: 2000,
      });
      return user;
    });
  };

  const onEditAvatar = a =>
    AvatarSource.uploadAvatar(editingUser?.user.userId, a).then(() => {
      setEditingUser({...editingUser, version: editingUser!.version! + 1});
      ToastActions.addToast({
        message: 'Avatar Updated',
        time: 2000,
      });
    });

  const onResetPassword = (u: User) =>
    PasswordSource.forgotPassword(u.email).then(() =>
      ToastActions.addToast({
        message: 'Password Reset Email Sent',
        time: 2000,
      })
    );

  return (
    <>
      <UserProfile
        user={editingUser.user}
        onEditUser={onEditUser}
        onEditAvatar={onEditAvatar}
        onResetPassword={onResetPassword}
      />
      <div className={'button-container'}>
        <Button
          variant="contained"
          color="primary"
          className="ev-button"
          disabled={isSaving || !formIsValid}
          onClick={onSaveUser}>
          Save
        </Button>
        <Button variant="outlined" className="ev-button" onClick={resetUser} disabled={isPristine}>
          Reset
        </Button>
      </div>
    </>
  );
};

const UserProfileContainerWrapper: React.FC = () => {
  return (
    <UserProfileContextProvider>
      <UserProfileContainer />
    </UserProfileContextProvider>
  );
};

export default UserProfileContainerWrapper;
